import { Alert, Button, Group, Modal, Select, Stack, Text } from '@mantine/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { config } from '../../../config';
import type { Practice, User } from '../types/rbac';
import { isStaffAddClientWideStep } from '../../public/intake/staffAddClientFlow';
import { StaffAddClientFlow } from './StaffAddClientFlow';
import { StaffAddClientSuccess } from './StaffAddClientSuccess';
import '../../public/intake/intake.css';

interface StaffAddClientModalProps {
  opened: boolean;
  onClose: () => void;
  /** Fixed practice (practice-scoped). Omit when platform admin must pick first. */
  practice?: Practice;
  practices?: Practice[];
  /** Optimistic list update when a client is created (avoid reload here — it unmounts this modal). */
  onCreated?: (user: User, context?: { organizationId: string }) => void;
}

/**
 * Single modal overlay on the current users page — no route change, no nested modal chrome.
 */
export function StaffAddClientModal({
  opened,
  onClose,
  practice: presetPractice,
  practices = [],
  onCreated,
}: StaffAddClientModalProps): JSX.Element | null {
  const [selectedPractice, setSelectedPractice] = useState<Practice | null>(presetPractice ?? null);
  const [pickerPracticeId, setPickerPracticeId] = useState<string | null>(null);
  const [successUser, setSuccessUser] = useState<User | null>(null);
  const [successClientName, setSuccessClientName] = useState('');
  const [flowSessionKey, setFlowSessionKey] = useState(0);
  const [activeStep, setActiveStep] = useState(0);

  const needsPracticePicker = !presetPractice && practices.length > 0;
  const activePractice = selectedPractice ?? presetPractice ?? null;
  const showSuccess = successUser != null && activePractice != null;

  const practiceOptions = useMemo(
    () => practices.map((p) => ({ value: p.id, label: p.name })),
    [practices],
  );

  useEffect(() => {
    if (!opened) {
      setSelectedPractice(presetPractice ?? null);
      setPickerPracticeId(null);
      setSuccessUser(null);
      setSuccessClientName('');
      setActiveStep(0);
    } else {
      setFlowSessionKey((key) => key + 1);
    }
  }, [opened, presetPractice]);

  const handleClose = useCallback(() => {
    setSuccessUser(null);
    setSuccessClientName('');
    onClose();
  }, [onClose]);

  const handleClientCreated = useCallback(
    (user: User, clientName: string, context: { organizationId: string }) => {
      setSuccessUser(user);
      setSuccessClientName(clientName);
      onCreated?.(user, context);
    },
    [onCreated],
  );

  if (!opened) return null;

  const showPicker = needsPracticePicker && !activePractice;
  const showSimpleContent = !config.medplum.projectId || showPicker || !activePractice;

  // Simple states (config error, practice picker) use the standard Mantine chrome.
  if (showSimpleContent) {
    return (
      <Modal opened onClose={handleClose} title="Add client" size="md" centered>
        {!config.medplum.projectId ? (
          <Alert color="red" title="Medplum project not configured">
            Set <code>VITE_MEDPLUM_PROJECT_ID</code> in the UI environment so client invites and intake
            bots can run.
          </Alert>
        ) : showPicker ? (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Select the practice this client belongs to.
            </Text>
            <Select
              label="Practice"
              placeholder="Choose a practice"
              data={practiceOptions}
              value={pickerPracticeId}
              onChange={setPickerPracticeId}
              searchable
              required
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                disabled={!pickerPracticeId}
                onClick={() => {
                  const match = practices.find((p) => p.id === pickerPracticeId);
                  if (match) setSelectedPractice(match);
                }}
              >
                Continue
              </Button>
            </Group>
          </Stack>
        ) : (
          <Alert color="yellow" title="Practice required">
            Choose a practice to continue.
          </Alert>
        )}
      </Modal>
    );
  }

  const isWideStep = isStaffAddClientWideStep(activeStep);

  // Wizard: match public client intake (IntakeModal) — no Mantine chrome, flow draws its own header/footer.
  return (
    <Modal
      opened
      onClose={handleClose}
      size="auto"
      centered
      classNames={{
        content: [
          'intake-modal-content',
          'intake-modal-content--adaptive',
          'intake-modal-content--staff-add-client',
          isWideStep ? 'intake-modal-content--results' : '',
          showSuccess ? 'intake-modal-content--complete' : '',
        ]
          .filter(Boolean)
          .join(' '),
        body: 'intake-modal-body intake-modal-body--flush',
        header: 'intake-modal-header-hidden',
      }}
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape
    >
      {showSuccess ? (
        <StaffAddClientSuccess
          clientName={successClientName}
          user={successUser}
          practice={activePractice}
          onClose={handleClose}
        />
      ) : (
        <StaffAddClientFlow
          key={flowSessionKey}
          practice={activePractice}
          onClose={handleClose}
          onClientCreated={handleClientCreated}
          onStepChange={setActiveStep}
        />
      )}
    </Modal>
  );
}
